/**
 * Dynamic Script Loader for GeoDirectory Maps
 *
 * Handles dynamic loading of map libraries when lazy load is enabled.
 * Loads Google Maps, Leaflet, or OSM scripts on-demand to improve performance.
 */

export class DynamicScriptLoader {
	/**
	 * Load map scripts dynamically based on the configured map API.
	 *
	 * @param {string} mapApi - Map API to use ('auto', 'google', or 'osm')
	 * @returns {Promise<string>} - Resolves with the loaded provider type ('google' or 'osm')
	 */
	static async loadMapScripts(mapApi) {

		// Get base plugin URL from existing script
		const baseUrl = this.getPluginBaseUrl();
		const suffix = this.getScriptSuffix();

		if (mapApi === 'google') {
			return await this.loadGoogleMaps(baseUrl, suffix);
		} else if (mapApi === 'osm') {
			return await this.loadOSMMaps(baseUrl, suffix);
		} else if (mapApi === 'auto') {
			// Try Google first, fallback to OSM if it fails
			try {
				return await this.loadGoogleMaps(baseUrl, suffix);
			} catch (error) {
				return await this.loadOSMMaps(baseUrl, suffix);
			}
		}

		throw new Error('Invalid map API specified: ' + mapApi);
	}

	/**
	 * Load Google Maps and required dependencies.
	 */
	static async loadGoogleMaps(baseUrl, suffix) {

		// Define Google Maps callback function BEFORE loading the script
		if (!window.geodirInitGoogleMap) {
			window.geodirInitGoogleMap = function() {
				window.geodirGoogleMapsCallback = true;
				try {
					if (window.jQuery) {
						jQuery(document).trigger('geodir.googleMapsCallback');
					}
				} catch (err) {
				}
			};
		}

		// Build Google Maps API URL
		const googleMapsUrl = this.buildGoogleMapsUrl();

		// Load scripts in order: Google Maps API -> Leaflet -> GoogleMutant -> OMS (if not single page)
		await this.loadScript('geodir-google-maps', googleMapsUrl);
		await this.loadScript('geodir-leaflet-script', `${baseUrl}/resources/vendor/maps/leaflet/leaflet${suffix}.js`);
		await this.loadScript('geodir-leaflet-googlemutant', `${baseUrl}/resources/vendor/maps/leaflet/Leaflet.GoogleMutant${suffix}.js`);

		// Only load OMS for directory/archive maps (not single post pages)
		const hasDirectoryMap = document.querySelector('[data-map-type="directory"], [data-map-type="archive"]');
		if (hasDirectoryMap) {
			await this.loadScript('geodir-overlapping-marker-spiderfier', `${baseUrl}/assets/js/overlapping-marker-spiderfier.js`);
		}

		// Load Leaflet CSS
		await this.loadStylesheet('geodir-leaflet-style', `${baseUrl}/resources/vendor/maps/leaflet/leaflet.css`);

		return 'google';
	}

	/**
	 * Load OSM/Leaflet maps and required dependencies.
	 */
	static async loadOSMMaps(baseUrl, suffix) {

		// Load scripts in order: Leaflet -> OSM Geocode -> OMS (if not single page)
		await this.loadScript('geodir-leaflet-script', `${baseUrl}/resources/vendor/maps/leaflet/leaflet${suffix}.js`);
		await this.loadScript('geodir-leaflet-geo-script', `${baseUrl}/resources/vendor/maps/leaflet/osm.geocode${suffix}.js`);

		// Only load OMS for directory/archive maps (not single post pages)
		const hasDirectoryMap = document.querySelector('[data-map-type="directory"], [data-map-type="archive"]');
		if (hasDirectoryMap) {
			await this.loadScript('geodir-overlapping-marker-spiderfier', `${baseUrl}/assets/js/overlapping-marker-spiderfier.js`);
		}

		// Load Leaflet CSS
		await this.loadStylesheet('geodir-leaflet-style', `${baseUrl}/resources/vendor/maps/leaflet/leaflet.css`);

		return 'osm';
	}

	/**
	 * Load a single script file.
	 */
	static loadScript(id, src) {
		return new Promise((resolve, reject) => {
			// Check if already loaded
			if (document.getElementById(id)) {
				resolve();
				return;
			}

			const script = document.createElement('script');
			script.id = id;
			script.src = src;
			script.type = 'text/javascript';

			script.onload = () => {
				resolve();
			};

			script.onerror = () => {
				reject(new Error(`Failed to load script: ${src}`));
			};

			document.head.appendChild(script);
		});
	}

	/**
	 * Load a single stylesheet file.
	 */
	static loadStylesheet(id, href) {
		return new Promise((resolve, reject) => {
			// Check if already loaded
			if (document.getElementById(id)) {
				resolve();
				return;
			}

			const link = document.createElement('link');
			link.id = id;
			link.rel = 'stylesheet';
			link.type = 'text/css';
			link.href = href;

			link.onload = () => {
				resolve();
			};

			link.onerror = () => {
				reject(new Error(`Failed to load stylesheet: ${href}`));
			};

			document.head.appendChild(link);
		});
	}

	/**
	 * Get the plugin base URL from existing scripts.
	 */
	static getPluginBaseUrl() {
		// Try to get from geodir_map_params
		if (window.geodir_map_params && window.geodir_map_params.plugin_url) {
			return window.geodir_map_params.plugin_url;
		}

		// Fallback: extract from geodir-maps.js script tag
		const script = document.querySelector('script[src*="geodir-maps.js"]');
		if (script) {
			const src = script.getAttribute('src');
			// Extract base URL (remove /assets/js/geodir-maps.js)
			return src.replace(/\/assets\/js\/geodir-maps\.js.*$/, '');
		}

		// Last resort: assume standard WordPress plugin path
		return '/wp-content/plugins/geodirectory';
	}

	/**
	 * Get the script suffix based on SCRIPT_DEBUG.
	 */
	static getScriptSuffix() {
		// Try to get from geodir_map_params (most reliable)
		if (window.geodir_map_params && window.geodir_map_params.script_suffix) {
			return window.geodir_map_params.script_suffix;
		}

		// Fallback: Check if we're loading minified scripts
		const script = document.querySelector('script[src*="geodir-maps"]');
		if (script && script.src.includes('.min.')) {
			return '.min';
		}
		return '';
	}

	/**
	 * Build Google Maps API URL with parameters.
	 */
	static buildGoogleMapsUrl() {
		// Get from existing Google Maps script if already present
		const existingScript = document.querySelector('script[src*="maps.google.com"]');
		if (existingScript) {
			return existingScript.src;
		}

		// Build from geodir_map_params if available
		if (window.geodir_map_params && window.geodir_map_params.google_maps_url) {
			return window.geodir_map_params.google_maps_url;
		}

		// Fallback: basic Google Maps API URL (will need API key from server)
		const language = document.documentElement.lang || 'en';
		return `https://maps.google.com/maps/api/js?language=${language}&libraries=places&callback=geodirInitGoogleMap`;
	}
}

